export const CONTENT_SCOPES = ['unassigned', 'all', 'brand', 'branch'];

/** Brand filter sentinel: Unassigned / none (not a real brand id). */
export const BRAND_FILTER_UNASSIGNED = '__unassigned__';

export const initialContentScopeFilter = () => ({
  scope: '',
  brandId: '',
  salonId: ''
});

export const hasActiveContentScopeFilter = (filters = {}) =>
  Boolean(filters.scope || filters.brandId || filters.salonId);

export const brandFilterSelectValue = (filters = {}, lockedBrandId = null) => {
  if (filters.scope === 'unassigned' || filters.visibility === 'unassigned') {
    return BRAND_FILTER_UNASSIGNED;
  }
  if (lockedBrandId && String(filters.brandId || '') === String(lockedBrandId)) {
    return '';
  }
  return filters.brandId || '';
};

/** Brand/branch query params for tenant-scoped admin list APIs. */
export const tenantListParams = (portal, filters = {}) => {
  const params = {};
  const brandId = filters.brandId === BRAND_FILTER_UNASSIGNED ? '' : filters.brandId;
  if (portal?.isSuperAdmin) {
    if (brandId) params.brandId = brandId;
    if (filters.salonId) params.salonId = filters.salonId;
  } else if (portal?.isBrandAdmin) {
    if (filters.salonId) params.salonId = filters.salonId;
  }
  return params;
};

/** List params including optional visibility scope filter. */
export const contentScopeListParams = (portal, filters = {}) => {
  const params = tenantListParams(portal, filters);
  if (portal?.isBranchAdmin) return params;
  if (filters.scope) params.scope = filters.scope;
  return params;
};

export const initialContentScopeForm = () => ({
  scope: 'unassigned',
  brandId: '',
  brandIds: [],
  salonId: '',
  salonIds: []
});

/** Defaults for create forms based on logged-in portal role. */
export const contentScopeFormForPortal = (portal) => {
  if (portal?.isBranchAdmin) {
    return {
      scope: 'branch',
      brandId: portal.brandId || '',
      brandIds: portal.brandId ? [portal.brandId] : [],
      salonId: portal.salonId || '',
      salonIds: portal.salonId ? [portal.salonId] : []
    };
  }
  if (portal?.isBrandAdmin) {
    return {
      scope: 'unassigned',
      brandId: portal.brandId || '',
      brandIds: portal.brandId ? [portal.brandId] : [],
      salonId: '',
      salonIds: []
    };
  }
  return initialContentScopeForm();
};

export const contentScopeAllowedScopes = (portal) => {
  if (portal?.isBranchAdmin) return ['branch'];
  if (portal?.isBrandAdmin) return ['unassigned', 'brand', 'branch'];
  return ['unassigned', 'all', 'brand', 'branch'];
};

const idOf = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

export const contentScopeFromItem = (item = {}) => {
  const brandIds = Array.isArray(item.brands) && item.brands.length
    ? item.brands.map(idOf).filter(Boolean)
    : (item.brand ? [idOf(item.brand)] : []);
  const salonIds = Array.isArray(item.salons) && item.salons.length
    ? item.salons.map(idOf).filter(Boolean)
    : (item.salon ? [idOf(item.salon)] : []);
  return {
    scope: item.scope || (item.brand || item.salon ? (item.salon ? 'branch' : 'brand') : 'unassigned'),
    brandId: brandIds[0] || '',
    brandIds,
    salonId: salonIds[0] || '',
    salonIds
  };
};

export const contentScopeToPayload = ({ scope, brandId, brandIds, salonId, salonIds }) => {
  const payload = { scope: scope || 'unassigned' };
  if (payload.scope === 'brand') {
    const ids = (brandIds && brandIds.length ? brandIds : brandId ? [brandId] : []).filter(Boolean);
    payload.brandIds = ids;
    if (ids[0]) payload.brandId = ids[0];
  } else if (payload.scope === 'branch') {
    const ids = (salonIds && salonIds.length ? salonIds : salonId ? [salonId] : []).filter(Boolean);
    payload.salonIds = ids;
    if (ids[0]) payload.salonId = ids[0];
  } else if (payload.scope === 'unassigned' && brandId) {
    payload.brandId = brandId;
    payload.brandIds = brandIds?.length ? brandIds : [brandId];
  }
  return payload;
};

export const formatContentScopeLabel = (item = {}) => {
  const scope = item.scope || 'unassigned';
  if (scope === 'unassigned') return 'Unassigned';
  if (scope === 'brand') {
    const list = Array.isArray(item.brands) && item.brands.length
      ? item.brands
      : item.brand
        ? [item.brand]
        : [];
    if (!list.length) return 'Brand-wide (no brand selected)';
    const names = list.map((b) => {
      const name = b?.name || 'Brand';
      const code = b?.brandCode ? ` (${b.brandCode})` : '';
      return `${name}${code}`;
    });
    return list.length === 1
      ? `All branches in brand: ${names[0]}`
      : `Brands: ${names.join(', ')}`;
  }
  if (scope === 'branch') {
    const list = Array.isArray(item.salons) && item.salons.length
      ? item.salons
      : item.salon
        ? [item.salon]
        : [];
    if (!list.length) return 'Branch-specific (none selected)';
    const names = list.map((s) => [s?.name, s?.branch].filter(Boolean).join(' ') || 'Branch');
    return list.length === 1
      ? `Branch only: ${names[0]}`
      : `Branches: ${names.join(', ')}`;
  }
  return 'All brands & branches';
};
